// utils/qwen-voice-visual-set.ts — P0/P1/P2 of the voice-visuals rebuild.
//
// THE single decision function for the voice call's "current visual set". It
// replaces the append-only marker-bubble planner family (RC1+RC2) with one
// source of truth: a CURRENT set of (max 3) signs that is REPLACED — never
// appended — on each new selection. The same set both renders in the overlay's
// dedicated visual slot AND feeds the model's `currentVisibleImages`, so what is
// on screen == what the model is told, by construction.
//
// P0b (same contract as text): topic resolution + candidate signs come from the
// SHARED visual registry (`qwen-voice-visual-registry.ts`) — the same id-based
// registry the text/BE path reasons over — NOT a parallel FE regex topic model.
// The brittle `detectTopicFromText` in qwen-voice-visual-planner.ts is retired as
// the decider; this module is the one tested seam.
//
// P1b (confidence floor + fail-closed): `detectQwenVoiceVisualTopicFromText`
// already computes high/medium/low; here it GATES the swap. A `>= medium`
// detection replaces the set; a `low`/`unknown` detection on a visual ASK asks a
// clarifying question (no stale claim); a non-visual turn keeps the set untouched
// and the model is told nothing changed. Singular/plural + deictic forms
// ('segnale' vs 'segnali', 'quello/quel segnale', 'fammelo vedere') are
// normalised so a turn-6-style follow-up routes to the visual/deictic path
// instead of silently falling into stale context.

import {
  detectQwenVoiceVisualTopicFromText,
  getQwenVoiceVisualAssetsForTopic,
  getQwenVoiceVisualTopicForImageId,
  normalizeQwenVoiceVisualText,
  type QwenVoiceVisualTopicId,
} from './qwen-voice-visual-registry';

/** A single sign in the current visual set (kind/id/alt mirror VoiceContextV1). */
export interface VoiceVisualSign {
  kind: 'sign';
  id: string;
  alt: string;
}

/** The CURRENT visual set shown in the call's dedicated slot (max 3 signs). */
export interface VoiceVisualSet {
  topicId: QwenVoiceVisualTopicId;
  signs: VoiceVisualSign[];
  /** The transaction that produced this set (telemetry / dedupe). */
  transactionId: string;
}

export type VoiceVisualShownIdsByTopic = Partial<Record<QwenVoiceVisualTopicId, string[]>>;

export interface VoiceVisualHistoryUpdate {
  topicId: QwenVoiceVisualTopicId;
  shownIds: string[];
  wrapped: boolean;
}

export type VoiceVisualAction =
  // Replace the current slot with a NEW set (topic switch / first show).
  | 'replace'
  // Re-assert the SAME visible topic (no flicker; set stays put).
  | 'reuse'
  // Non-visual turn: keep whatever is shown, tell the model nothing changed.
  | 'keep'
  // Visual ask but the topic is too weak to commit: ask a clarifying question,
  // keep the set, and DO NOT claim a (possibly stale) sign is the answer.
  | 'clarify';

export interface VoiceVisualDecision {
  action: VoiceVisualAction;
  /** The set to render after this turn (null when nothing is/should be shown). */
  nextSet: VoiceVisualSet | null;
  /** What the model must be told the student is seeing now. */
  renderStatus: 'shown_new' | 'unchanged' | 'none';
  topicId: QwenVoiceVisualTopicId;
  confidence: 'high' | 'medium' | 'low';
  /** Italian clarify prompt (in Il Prof's voice) when action === 'clarify'. */
  clarifyMessage?: string;
  /** Updated same-topic shown history when action === 'replace'. */
  historyUpdate?: VoiceVisualHistoryUpdate;
  reason: string;
}

const MAX_SET_SIGNS = 3;

// Visual-ask + deictic detection, normalised so singular/plural and pronoun
// forms all match. Crucially includes the singular 'segnale' and the deictic
// 'quello/quel segnale' / 'fammelo/fammeli vedere' that the old routers dropped
// (the exact turn-6 miss: "Non mi hai mostrato il segnale: puoi riprendere da
// quello?").
const VISUAL_NOUN_RE = /\b(?:cartell[oi]|segnal[ei]|immagin[ei]|foto|figur[ae]|visuale)\b/;
const SHOW_VERB_RE = /\b(?:mostr\w*|fammel[oi]|fammi\s+vedere|farmi\s+vedere|far\s+vedere|vedere|veder\w*|riprend\w*|riprese)\b/;
// Pronoun/clitic deictics. A clitic show-verb (mostrameli/fammelo/vederli) already
// CARRIES a deictic referent, so those are matched here too — this is the exact
// turn-6 family ("riprendi da quello", "fammelo vedere") the old routers dropped.
const DEICTIC_RE = /\b(?:quell[oiae]|quei|quest[oiae]|mostram\w*|fammel[oi]|vederl[oi]|melo|meli|mele)\b/;
// A request explicitly NOT wanting images — never swap or show on these.
const NO_IMAGE_RE = /\b(?:senza\s+(?:immagini|foto|figure|cartelli)|solo\s+testo)\b/;
// Weather-modified speed limit is a RULE with no sign — never surface a generic
// speed sign for it (the "sometimes the wrong one" bug). Keep teaching the rule.
const WEATHER_RE = /\b(?:piogg\w*|piove|bagnat\w*|nebbi\w*|neve|nevic\w*|ghiacc\w*)\b/;
const SPEED_WORD_RE = /\b(?:limit\w*|velocit\w*|130|110|km\s?h)\b/;
// "altri / diversi" → the student wants DIFFERENT same-topic signs.
const OTHER_RE = /\b(?:altr[oie]|divers[ie]|collegat[ioe]|oltre|ancora)\b/;

function hashSeed(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36).padStart(6, '0').slice(0, 8);
}

function uniqueNormalizedIds(ids: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of ids) {
    const normalized = normalizeQwenVoiceVisualText(id);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(id);
  }
  return result;
}

function shownHistorySeed(shownIdsByTopic: VoiceVisualShownIdsByTopic): string {
  return Object.entries(shownIdsByTopic)
    .filter(([, ids]) => Array.isArray(ids) && ids.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([topicId, ids]) => `${topicId}:${uniqueNormalizedIds(ids ?? []).join('|')}`)
    .join(';');
}

/**
 * Does this utterance ASK for a visual? True when it names a visual noun, or is a
 * deictic follow-up ('mostrameli', 'fammelo vedere', 'riprendi da quello') whose
 * referent is a visual noun in the recent context or an already-shown set.
 */
export function isVoiceVisualAsk(userText: string, contextText: string, hasCurrentSet: boolean): boolean {
  const u = normalizeQwenVoiceVisualText(userText);
  if (!u || NO_IMAGE_RE.test(u)) return false;
  if (VISUAL_NOUN_RE.test(u) && SHOW_VERB_RE.test(u)) return true;
  if (VISUAL_NOUN_RE.test(u)) return true;
  // Deictic-only follow-up ("fammelo vedere", "riprendi da quello") — only a
  // visual ask when there is a visual referent to point at.
  if (SHOW_VERB_RE.test(u) && DEICTIC_RE.test(u)) {
    const c = normalizeQwenVoiceVisualText(contextText);
    return hasCurrentSet || VISUAL_NOUN_RE.test(c);
  }
  return false;
}

function topicOfSet(set: VoiceVisualSet | null): QwenVoiceVisualTopicId {
  if (!set) return 'unknown';
  if (set.topicId !== 'unknown') return set.topicId;
  for (const sign of set.signs) {
    const t = getQwenVoiceVisualTopicForImageId(sign.id);
    if (t !== 'unknown') return t;
  }
  return 'unknown';
}

function buildSet(
  topicId: QwenVoiceVisualTopicId,
  userText: string,
  currentSet: VoiceVisualSet | null,
  shownIdsByTopic: VoiceVisualShownIdsByTopic,
  transactionId: string,
): { set: VoiceVisualSet; historyUpdate: VoiceVisualHistoryUpdate } | null {
  const u = normalizeQwenVoiceVisualText(userText);
  const currentIds = currentSet?.signs.map((s) => s.id) ?? [];
  const asksForOther = OTHER_RE.test(u);
  const currentTopic = topicOfSet(currentSet);
  let priorShownIds = uniqueNormalizedIds([
    ...(shownIdsByTopic[topicId] ?? []),
    ...(currentTopic === topicId ? currentIds : []),
  ]);

  const allAssets = getQwenVoiceVisualAssetsForTopic(topicId);
  let assets = allAssets;
  let wrapped = false;
  if (asksForOther) {
    assets = getQwenVoiceVisualAssetsForTopic(topicId, { excludeIds: priorShownIds });
    if (assets.length === 0 && priorShownIds.length > 0) {
      // Exhausted this topic: make the wrap explicit and restart from the top
      // instead of silently dropping excludeIds in a fallback branch.
      priorShownIds = [];
      assets = allAssets;
      wrapped = true;
    }
  }
  if (assets.length === 0) return null;

  const signs: VoiceVisualSign[] = assets.slice(0, MAX_SET_SIGNS).map((asset) => ({
    kind: 'sign',
    id: asset.id,
    alt: asset.id,
  }));
  const shownIds = uniqueNormalizedIds([
    ...(asksForOther ? priorShownIds : []),
    ...signs.map((sign) => sign.id),
  ]);
  return {
    set: { topicId, signs, transactionId },
    historyUpdate: { topicId, shownIds, wrapped },
  };
}

/**
 * Resolve the CURRENT visual set for this turn — the single show/replace/keep
 * decision, gated by topic confidence. PURE + deterministic for a given input,
 * so it is unit-testable as the one seam every caller goes through.
 */
export function resolveVoiceVisualSet({
  userText,
  contextText = '',
  currentSet = null,
  shownIdsByTopic = {},
}: {
  userText: string;
  contextText?: string;
  currentSet?: VoiceVisualSet | null;
  shownIdsByTopic?: VoiceVisualShownIdsByTopic;
}): VoiceVisualDecision {
  const clean = userText.trim();
  const transactionId = `vset-${hashSeed(`${clean}\n${contextText}\n${shownHistorySeed(shownIdsByTopic)}`)}`;
  const u = normalizeQwenVoiceVisualText(clean);

  const keep = (reason: string): VoiceVisualDecision => ({
    action: 'keep',
    nextSet: currentSet,
    renderStatus: currentSet ? 'unchanged' : 'none',
    topicId: topicOfSet(currentSet),
    confidence: 'low',
    reason,
  });

  if (!clean) return keep('empty utterance');

  const visualAsk = isVoiceVisualAsk(clean, contextText, Boolean(currentSet));
  if (!visualAsk) return keep('latest turn is not a visual request');

  // A weather-modified speed-limit ask is a RULE with no sign: never show a
  // generic speed sign. Keep the current set and let Il Prof teach the rule.
  if (WEATHER_RE.test(u) && SPEED_WORD_RE.test(u)) {
    return {
      ...keep('weather-modified speed limit is a rule with no sign'),
      action: 'clarify',
      clarifyMessage:
        "Con pioggia, nebbia o neve il limite si abbassa per regola, non per un cartello diverso: te lo spiego a voce. Va bene?",
    };
  }

  const detected = detectQwenVoiceVisualTopicFromText(clean);
  let topicId = detected.topicId;
  let confidence = detected.confidence;
  let reason = 'topic from latest utterance';

  // P1b confidence floor: a topic-less / low-confidence visual ask is only routed
  // when it is a genuine FOLLOW-UP with a real referent — (a) a deictic follow-up
  // about what's already shown ("fammelo vedere"), or (b) a generic visual ask
  // ("fammi vedere qualche segnale") whose topic is recoverable from the recent
  // CONTEXT (the same thread the student is continuing). A bare ambiguous ask with
  // no referent fails CLOSED (clarify), never a stale/guessed sign.
  if (topicId === 'unknown' || confidence === 'low') {
    const currentTopic = topicOfSet(currentSet);
    const deictic = DEICTIC_RE.test(u) || (SHOW_VERB_RE.test(u) && !VISUAL_NOUN_RE.test(u));
    const fromContext = detectQwenVoiceVisualTopicFromText(contextText);
    if (currentTopic !== 'unknown' && deictic) {
      topicId = currentTopic;
      confidence = 'medium';
      reason = 'deictic follow-up reused the current visible topic';
    } else if (fromContext.topicId !== 'unknown') {
      // Generic visual follow-up — recover the topic from the recent conversation.
      topicId = fromContext.topicId;
      confidence = 'medium';
      reason = 'visual follow-up recovered topic from recent context';
    } else {
      // Fail-closed: a visual ask we cannot confidently route. Ask, do not show a
      // stale or guessed sign and do not claim it answers the question.
      return {
        action: 'clarify',
        nextSet: currentSet,
        renderStatus: currentSet ? 'unchanged' : 'none',
        topicId: 'unknown',
        confidence: 'low',
        clarifyMessage:
          'Dimmi di quale segnale o argomento parliamo — per esempio limiti, precedenza o sorpasso — e te lo mostro subito.',
        reason: 'visual ask below confidence floor; asking to clarify',
      };
    }
  }

  const currentTopic = topicOfSet(currentSet);
  const sameTopicVisible = currentTopic !== 'unknown' && currentTopic === topicId;
  const asksForOther = OTHER_RE.test(u);

  // Reuse: re-asked the SAME visible topic without asking for "altri" — keep the
  // set stable so the slot doesn't flicker; tell the model nothing changed.
  if (sameTopicVisible && !asksForOther) {
    return {
      action: 'reuse',
      nextSet: currentSet,
      renderStatus: 'unchanged',
      topicId,
      confidence,
      reason: 'same visible topic re-asked (reuse current set)',
    };
  }

  const next = buildSet(topicId, clean, currentSet, shownIdsByTopic, transactionId);
  if (!next) {
    // We are confident about the topic but have no registered assets for it.
    return {
      action: 'clarify',
      nextSet: currentSet,
      renderStatus: currentSet ? 'unchanged' : 'none',
      topicId,
      confidence,
      clarifyMessage:
        'Per questo punto non ho un cartello da mostrarti, ma te lo spiego a voce: vuoi che partiamo da lì?',
      reason: 'confident topic but no registered assets',
    };
  }

  return {
    action: 'replace',
    nextSet: next.set,
    renderStatus: 'shown_new',
    topicId,
    confidence,
    historyUpdate: next.historyUpdate,
    reason,
  };
}

/** The set's signs as VoiceContextV1.currentVisibleImages (the model's belief). */
export function visualSetToVisibleImages(
  set: VoiceVisualSet | null,
): Array<{ kind: 'sign'; id: string; alt: string }> | undefined {
  if (!set || set.signs.length === 0) return undefined;
  return set.signs.map((s) => ({ kind: 'sign', id: s.id, alt: s.alt }));
}
